import { Button, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import TextBoxInCell from '../../components/TextBoxInCell/TextBoxInCell';
import { useStore } from '../../hooks/use-store';
import { Contact } from '../../types/practice-types';

const PHONE_LENGTH = 10;
const DEFAULT_FIELD_LENGTH = 50;

interface RowCellRendererProps extends GridRendererProps<Contact> {
  field: keyof Contact;
}

const RowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const { apiStore, confirmationStore: store } = useStore();

  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (field === 'name' || index === 0) {
      ref.current?.focus();
    }
  }, [field, index]);

  const changeValue = (fieldToChange: keyof Contact, value: string): void => {
    if (fieldToChange === 'phone' && !/^\d*$/.test(value)) return;
    runInAction(() => {
      store.currentContact[fieldToChange] = value;
    });
  };

  const mainEmailDifferent = store.currentContacts[0].email !== apiStore.userEmail;

  if (
    store.currentIndex === index &&
    !(index === 0 && ['name', 'email'].includes(field) && !mainEmailDifferent)
  ) {
    return (
      <FieldGroupInCell
        htmlFor={`cell-text-box-${index}_${field}`}
        errorText={index === store.currentIndex ? store.contactError[field] : undefined}
      >
        <TextBox
          id={`cell-text-box-${index}_${field}`}
          ref={ref}
          value={store.currentContact[field] || ''}
          maxLength={field === 'phone' ? PHONE_LENGTH : DEFAULT_FIELD_LENGTH}
          onChange={e => changeValue(field, e.target.value)}
          data-testid={`contact-${field}-${store.currentContact.email}-input`}
        />
      </FieldGroupInCell>
    );
  }
  if (index > 0 || field !== 'phone' || mainEmailDifferent) {
    return (
      <div
        className='plain-text'
        role='table-cell-text'
        aria-label={`Contact ${field}`}
        data-testid={`contact-${field}-${store.currentContact.email}-div`}
      >
        {row[field] || ' '}
      </div>
    );
  }
  return (
    <TextBoxInCell
      id={`cell-text-box-${index}_${field}`}
      disabled
      value={store.currentContacts[index].phone || ''}
      placeholder='Add Phone'
      data-testid={`contact-phone-${store.currentContact.email}-input`}
    />
  );
});

export const ActionsRenderer = observer((props: GridRendererProps<Contact>) => {
  const { row, index } = props;
  const store = useStore().confirmationStore;
  const editMode = store.currentIndex !== undefined;
  if (store.currentIndex === index)
    return (
      <>
        <Button
          variant='link'
          size='small'
          iconOnly='save'
          onClick={() => store.validateAndUpdateContact()}
          title='Save changes'
          aria-label='Save Contact'
          data-testid={`save-${row.email}-btn`}
        />
        <Button
          variant='link'
          iconOnly='cancel'
          onClick={() => store.cancelEditingContact()}
          title='Cancel changes'
          aria-label='Cancel Changes'
          data-testid={`cancel-${row.email}-btn`}
        />
      </>
    );
  return (
    <>
      <Button
        variant='link'
        iconOnly='edit'
        disabled={editMode}
        onClick={() => store.editContact(index)}
        title='Edit contact'
        aria-label='Edit Contact'
        data-testid={`edit-${row.email}-btn`}
      />
      {index > 0 && (
        <Button
          variant='link'
          iconOnly='delete'
          disabled={editMode || index === 0}
          onClick={() => store.deleteContact(index)}
          title='Delete contact'
          aria-label='Delete Contact'
          data-testid={`delete-${row.email}-btn`}
        />
      )}
    </>
  );
});

export const rowCellRenderer =
  (field: keyof Contact): GridRenderer<Contact> =>
  (props: GridRendererProps<Contact>) => (
    <RowCellRenderer row={props.row} index={props.index} field={field} />
  );

export const MessageChosenTimeUnavailable = (): React.JSX.Element => (
  <>
    <p>
      MyCornerstone will be <strong>unavailable</strong> during the time you have selected.
    </p>
    <p>
      Click <strong>OK</strong> to return and schedule a new time.
    </p>
  </>
);
